<template lang="pug">
.trial-container
  div(v-if="!isTrialLimitMet")
    .trial-header
      p.heading-h5(v-if="isVirtual") {{__(`Start with a ${trialDurationDays}-Day Free Trial`)}}
      p.heading-h5(v-else) {{__(`${trialDurationDays}-Day Free Trial Included`)}}
      Icon.trial-icon(name="bell" fill="white")
    .trial-body
      div(v-html="__(productDescription)")
</template>
<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useProductStore } from '@/js/stores/productStore'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'

const props = defineProps<{buyRequest: BuyRequest, trialProduct: Product}>()

const customerStore = useCustomerStore()
const productStore = useProductStore()
const rogue = useRogue()
const parentProductId = ref(0)
const purchaseCount = ref(0)

const products = computed(() => productStore.products)
const loggedIn = computed(() => customerStore.loggedIn)
const product = computed(() => products.value[props.buyRequest.productId] || { attributes: {} })
const isVirtual = computed(() => product.value.type === 'virtual')
const trialDurationDays = computed(() => props.trialProduct.attributes.trial_duration_days || 0)
const renewalDate = computed(() => +trialDurationDays.value + 1)
const isTrialLimitMet = computed(() => (purchaseCount.value ? purchaseCount.value >= (props.trialProduct.attributes.purchase_limit as number) : false))

const rbProps = computed(() => {
  if (!props.buyRequest.metadata?.groupedProductId) {
    return rogue.getProductById(props.buyRequest.productId).roguebuilder?.props
  }

  return rogue.getProductById(props.buyRequest.metadata?.groupedProductId).roguebuilder?.props
})

// trialProductName and trialProductDescription are set in Roguebuilder
const productDescription = computed(() => {
  // The productName will only be used if trialProductDescription is not set for physical products
  // Digital products don't utilize productName per UX comps
  const productName = rbProps.value?.trialProductName || props.trialProduct?.name

  if (rbProps.value?.trialProductDescription) {
    return rbProps.value.trialProductDescription
  }

  if (isVirtual.value) {
    return `Train for free for the first ${trialDurationDays.value} days of your plan. Automatic billing begins on day ${renewalDate.value} if you remain subscribed.`
  }

  return `A free trial to <a href='${props.trialProduct?.url}' class='trial-product-link'>${productName}</a> subscription is included with purchase of this product. Your trial begins after you activate it.`
})

async function checkPurchaseLimitCount() {
  await customerStore.init(rogue)

  if (!loggedIn.value) {
    return
  }

  try {
    const data = await rogue.fetch<{[key: string] : { count: number}}>('/svc_checkout/order/history', 'post', { items: [props.trialProduct.sku] })

    purchaseCount.value = data[props.trialProduct.sku]?.count || 0
  } catch (error) {
    rogue.log.error('Error loading order history', error as Error)
  }
}

watch(loggedIn, async () => {
  if (!isVirtual.value) {
    return
  }

  await checkPurchaseLimitCount()

  if (!loggedIn.value) {
    props.buyRequest.productId = props.trialProduct.productId
    props.buyRequest.metadata = {}
    props.buyRequest.metadata.groupedProductId = products.value[parentProductId.value].productId
    purchaseCount.value = 0
  }
})

onMounted(async () => {
  // Dont switch the buyRequest on physical products
  if (!isVirtual.value) {
    return
  }

  parentProductId.value = product.value.productId
  await checkPurchaseLimitCount()

  if (!isTrialLimitMet.value) {
    props.buyRequest.productId = props.trialProduct.productId
    props.buyRequest.metadata = {}
    props.buyRequest.metadata.groupedProductId = products.value[parentProductId.value].productId
  }
})

</script>
<style lang="scss" scoped>
.trial-header {
  height: $space-xl;
  text-transform: uppercase;
  color: white;
  background-color: black;
  display: flex;
  align-items: center;

  .heading-h5 {
    margin: auto 0;
    text-transform: uppercase;
  }

  *:nth-child(1) {
    padding-left: $size-xxs;
  }

  .trial-icon {
    margin-left: auto;
    margin-right: $size-xxs
  }
}

.trial-body {
  padding: $size-xxs;
  font-size: $size-xxs;
  line-height: $size-m;
  border: 1px solid #d2d2d2;
}

</style>

<style lang="scss">
.trial-product-link {
  color: black;
  font-weight: $weight-bold;
  }

  .trial-product-link:hover {
    color: black;
  }

</style>
